/**
 * format.ts — `Intl`-based formatters bound to a live locale getter.
 *
 * Zero dependencies: `Intl.DateTimeFormat` / `NumberFormat` / `PluralRules` are
 * web-standard and cover the plural/date/number/currency needs an ICU library
 * would (the ADR's rejected-ICU decision). Each call reads the active locale
 * from the getter, so a formatter follows locale switches without rebinding.
 */
import type { Locale } from "./types.ts";

export interface LocaleFormatters {
  /** Format a `Date` or epoch-millis timestamp per the active locale. */
  date(value: Date | number, options?: Intl.DateTimeFormatOptions): string;
  /** Format a number per the active locale. */
  number(value: number, options?: Intl.NumberFormatOptions): string;
  /** Format a monetary amount; currency defaults to EUR (the product's currency). */
  currency(value: number, currency?: string, options?: Intl.NumberFormatOptions): string;
  /** The CLDR plural category (`"one"`, `"other"`, …) for a count in the active locale. */
  plural(value: number, options?: Intl.PluralRulesOptions): Intl.LDMLPluralRule;
}

export function createFormatters(getLocale: () => Locale): LocaleFormatters {
  return {
    date(value, options) {
      return new Intl.DateTimeFormat(getLocale(), options).format(value);
    },
    number(value, options) {
      return new Intl.NumberFormat(getLocale(), options).format(value);
    },
    currency(value, currency = "EUR", options) {
      return new Intl.NumberFormat(getLocale(), {
        ...options,
        style: "currency",
        currency,
      }).format(value);
    },
    plural(value, options) {
      return new Intl.PluralRules(getLocale(), options).select(value);
    },
  };
}
